/**
 * Design Tokens — Vidal Refined
 *
 * Sistema central de tokens visuais usado pelos styled-components.
 * Combina identidade Vidal (verde esmeralda + dark) com estética
 * Linear/Vercel + Stripe (minimalismo + sombras multicamada + tabular-nums).
 *
 * Helper: surface(theme, level) — retorna surface compatível com light/dark.
 */

export const tokens = {
  // ============ BRAND ============
  brand:        "#10b981",
  brandHover:   "#0ea372",
  brandStrong:  "#059669",
  brandSoft:    "rgba(16,185,129,0.10)",
  brandRing:    "rgba(16,185,129,0.28)",
  brandGlow:    "rgba(16,185,129,0.18)",

  // ============ SEMANTIC (usar só em estados categóricos) ============
  info:         "#3b82f6",
  infoSoft:     "rgba(59,130,246,0.10)",
  warn:         "#f59e0b",
  warnSoft:     "rgba(245,158,11,0.10)",
  danger:       "#ef4444",
  dangerSoft:   "rgba(239,68,68,0.10)",
  neutral:      "#64748b",

  // ============ SURFACES (dark) ============
  surface0Dark: "rgba(255,255,255,0.025)",
  surface1Dark: "rgba(255,255,255,0.045)",
  surface2Dark: "rgba(255,255,255,0.07)",
  hairlineDark: "rgba(255,255,255,0.07)",
  hairlineStrongDark: "rgba(255,255,255,0.12)",

  // ============ SURFACES (light) ============
  // No light mode, body é cinza muito claro (#fafbfc).
  // Surfaces são variações de BRANCO (mais claras que o body) para criar
  // elevação ascendente. surface2 é uma exceção (track interno levemente
  // mais escuro, usado em progress bars / fundos de inputs etc).
  surface0Light: "#ffffff",
  surface1Light: "#fbfcfd",
  surface2Light: "#f1f5f9",
  hairlineLight: "rgba(15,23,42,0.07)",
  hairlineStrongLight: "rgba(15,23,42,0.14)",

  // ============ SOMBRAS (multicamada) ============
  shadowSm:    "0 1px 2px rgba(0,0,0,0.25)",
  shadowMd:    "0 2px 4px rgba(0,0,0,0.18), 0 8px 24px rgba(0,0,0,0.25)",
  shadowLg:    "0 4px 8px rgba(0,0,0,0.22), 0 16px 48px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.05)",
  shadowGlow:  "0 0 0 1px rgba(16,185,129,0.22), 0 8px 32px rgba(16,185,129,0.18)",
  shadowDanger:"0 0 0 1px rgba(239,68,68,0.22), 0 8px 32px rgba(239,68,68,0.18)",

  // ============ MOTION (Linear-style easings) ============
  easeOut:     "cubic-bezier(0.16, 1, 0.3, 1)",
  easeInOut:   "cubic-bezier(0.45, 0, 0.55, 1)",
  easeSpring:  "cubic-bezier(0.34, 1.56, 0.64, 1)",
  durFast:     "120ms",
  durBase:     "200ms",
  durSlow:     "320ms",

  // ============ FONTS ============
  fontSans:    "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontMono:    "'JetBrains Mono', 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace",

  // ============ RADII ============
  radiusXs:    "6px",
  radiusSm:    "8px",
  radiusMd:    "12px",
  radiusLg:    "16px",
  radiusXl:    "20px",
  radiusPill:  "999px",

  // ============ SPACING (escala 4px) ============
  s1:  "4px",
  s2:  "8px",
  s3:  "12px",
  s4:  "16px",
  s5:  "20px",
  s6:  "24px",
  s8:  "32px",
  s10: "40px",
  s12: "48px",

  // ============ TYPOGRAPHY SCALE ============
  fsEyebrow:   "0.68em",   // mono uppercase tiny labels
  fsLabel:     "0.78em",
  fsBody:      "0.9em",
  fsTitle:     "1.05em",
  fsH3:        "1.4em",
  fsH2:        "1.8em",
  fsHero:      "2.4em",    // KPI hero numbers

  // ============ EFFECTS ============
  blurBg:      "blur(12px)",
  tabularNums: "tabular-nums",
};

/**
 * Retorna surface no nível pedido respeitando light/dark do theme.
 * level: 0|1|2 — base/elevated/hover
 */
export function surface(theme, level = 0) {
  const isLight = theme && theme.body === "#fafbfc";
  if (level === 2) return isLight ? tokens.surface2Light : tokens.surface2Dark;
  if (level === 1) return isLight ? tokens.surface1Light : tokens.surface1Dark;
  return isLight ? tokens.surface0Light : tokens.surface0Dark;
}

/**
 * Hairline border respeitando light/dark.
 */
export function hairline(theme, strong = false) {
  const isLight = theme && theme.body === "#fafbfc";
  if (strong) return isLight ? tokens.hairlineStrongLight : tokens.hairlineStrongDark;
  return isLight ? tokens.hairlineLight : tokens.hairlineDark;
}

/**
 * Mono numeric helper for inline use in styled blocks.
 */
export const monoNum = `
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

export default tokens;
